import React, { useState } from "react";
import { Modal, useSnackBar } from "@truevault/aquilo";
import {
  CollectionContext,
  OrganizationDataRecipientDto,
  OrganizationDataSourceDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { Api } from "../../../../common/service/Api";
import { VENDOR_CATEGORY_AD_NETWORK } from "../../../types/Vendor";
import { addRecipientContextSettings } from "../../../util/vendors";
import {
  VendorRecipientDisclosures,
  VendorRoleDefinition,
} from "../vendor-inventory/VendorRecipientDisclosures";

export type SourceRecipientPrompt = {
  source: OrganizationDataSourceDto;
  /** Present when the source was already added as a recipient — consumer ad networks always are. */
  recipient?: OrganizationDataRecipientDto;
  /** A recipient in the other context: adding it here keeps its settings. */
  existingRecipient?: OrganizationDataRecipientDto;
};

/**
 * Follows adding a source that isn't in this context's Vendor List. A consumer ad network is always
 * a recipient too (its pixel or SDK receives data from the site), so it arrives already added and
 * this goes straight to its disclosures. Any other source (on HR, every source) is asked whether it
 * also receives data; a yes adds it to the Vendor List and then asks for its disclosures.
 */
export function SourceRecipientPromptModal({
  org,
  prompt,
  collectionContext,
  onAddedAsRecipient,
  onClose,
}: {
  org: OrganizationDto;
  prompt: SourceRecipientPrompt;
  collectionContext: CollectionContext;
  /** Lets the host refresh what it derives from the Vendor List. */
  onAddedAsRecipient: () => void;
  onClose: () => void;
}) {
  const { source } = prompt;
  const showSnackbar = useSnackBar();
  const [recipient, setRecipient] = useState<OrganizationDataRecipientDto | null>(
    prompt.recipient ?? null,
  );
  const [isAdding, setIsAdding] = useState(false);
  // Only a consumer ad network arrives already added; one added from the prompt reads like any other.
  const wasAddedAsAdNetwork =
    prompt.recipient != null && source.category === VENDOR_CATEGORY_AD_NETWORK;
  const isAsking = recipient == null;

  const addAsRecipient = async () => {
    setIsAdding(true);
    try {
      const added = await Api.organizationVendor.addOrUpdateVendor(
        org.id,
        source.vendorId,
        addRecipientContextSettings([collectionContext], prompt.existingRecipient),
      );
      setRecipient(added);
      onAddedAsRecipient();
    } catch {
      showSnackbar(`Couldn't add ${source.name} as a recipient. Please try again.`, {
        variant: "error",
      });
    } finally {
      setIsAdding(false);
    }
  };

  const body = isAsking
    ? `Some sources also receive personal data from your business. If you share data with ${source.name}, add it to your Vendor List as a recipient.`
    : wasAddedAsAdNetwork
    ? `Ad networks usually also receive data from your site, through a pixel, SDK or audience upload, so ${source.name} was added to your Vendor List as a data recipient. Choose the data your business shares with it.`
    : `${source.name} was added to your Vendor List. Choose the data your business shares with it.`;

  return (
    <Modal
      title={
        isAsking
          ? `Is ${source.name} also a data recipient?`
          : `${source.name} is also a data recipient`
      }
      body={body}
      alignText="left"
      alignButton="right"
      firstButton={isAsking ? "No, only a source" : undefined}
      onFirstButtonClick={onClose}
      secondButton={isAsking ? (isAdding ? "Adding…" : "Yes, add as recipient") : "Done"}
      secondButtonColor="primary"
      secondButtonDisabled={isAdding}
      onSecondButtonClick={isAsking ? addAsRecipient : onClose}
      showOverlay
      closeButton
      onClose={onClose}
      panelStyle={{ maxWidth: 640 }}
      contentContainerClasses="w-full"
    >
      <div className="w-full">
        {isAsking ? (
          <VendorRoleDefinition role="recipient" />
        ) : (
          <div className="overflow-y-auto" style={{ maxHeight: "50vh", paddingRight: 4 }}>
            <VendorRecipientDisclosures
              vendor={recipient}
              org={org}
              collectionContext={collectionContext}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
